import type { Vec3 } from '@battle/sim';
import * as THREE from 'three/webgpu';

/**
 * Third-person spring-arm camera (docs/02 §3): over-the-right-shoulder, collision-aware,
 * with lock-on framing and presentation-only juice (shake, FOV kick). Owns the look yaw/pitch
 * the player steers, which the sim uses as the movement frame.
 */
export interface CameraTuning {
  distance: number;
  height: number;
  shoulder: number;
  pitchMin: number;
  pitchMax: number;
  followLag: number; // seconds to close ~63% of the gap
  collisionRadius: number;
  pullIn: number; // m/s-ish responsiveness when colliding (fast)
  easeOut: number; // when the obstacle clears (slow)
  fov: number;
  sprintFov: number;
  lockOnFraming: number; // 0..1: how much the target pulls the look direction
}

export const DEFAULT_CAMERA: CameraTuning = {
  distance: 5.2,
  height: 1.35,
  shoulder: 0.55,
  pitchMin: -0.95,
  pitchMax: 0.6,
  followLag: 0.07,
  collisionRadius: 0.25,
  pullIn: 30,
  easeOut: 4,
  fov: 52,
  sprintFov: 60,
  lockOnFraming: 0.85,
};

export interface FollowCamera {
  tuning: CameraTuning;
  yaw: number;
  pitch: number;
  /** Scale the arm with creature size (1 = Kilnback). */
  scale: number;
  lockTarget: (() => Vec3) | null;
  /** Add screen-shake trauma 0..1 (decays). */
  addTrauma(amount: number): void;
  update(
    dt: number,
    focus: Vec3,
    opts: { sprinting: boolean; look: { yaw: number; pitch: number } },
  ): void;
}

const tmp = new THREE.Vector3();

export function createFollowCamera(
  camera: THREE.PerspectiveCamera,
  castRay: (o: Vec3, d: Vec3, max: number) => number | null,
  reduceMotion = false,
): FollowCamera {
  const smoothed = new THREE.Vector3();
  let initialized = false;
  let armLength = DEFAULT_CAMERA.distance;
  let trauma = 0;
  let t = 0;

  const cam: FollowCamera = {
    tuning: { ...DEFAULT_CAMERA },
    yaw: 0,
    pitch: -0.18,
    scale: 1,
    lockTarget: null,
    addTrauma(a) {
      trauma = Math.min(1, trauma + a);
    },
    update(dt, focus, { sprinting, look }) {
      const k = cam.tuning;
      t += dt;
      cam.pitch = THREE.MathUtils.clamp(cam.pitch + look.pitch, k.pitchMin, k.pitchMax);
      cam.yaw += look.yaw;

      // Follow point: creature position at shoulder height, lightly lagged for weight.
      const fx = focus.x;
      const fy = focus.y + k.height * cam.scale;
      const fz = focus.z;
      if (!initialized) {
        smoothed.set(fx, fy, fz);
        initialized = true;
      }
      const a = 1 - Math.exp(-dt / Math.max(1e-3, k.followLag));
      smoothed.x += (fx - smoothed.x) * a;
      smoothed.z += (fz - smoothed.z) * a;
      smoothed.y += (fy - smoothed.y) * Math.min(1, a * 0.6);

      // Lock-on: steer yaw (and a bit of pitch) so the target sits near screen center.
      if (cam.lockTarget) {
        const tg = cam.lockTarget();
        const dx = tg.x - smoothed.x;
        const dz = tg.z - smoothed.z;
        const want = Math.atan2(dx, dz);
        let diff = want - cam.yaw;
        diff = Math.atan2(Math.sin(diff), Math.cos(diff));
        cam.yaw += diff * Math.min(1, dt * 8 * k.lockOnFraming);
        const flat = Math.hypot(dx, dz);
        const wantPitch = THREE.MathUtils.clamp(
          -Math.atan2(smoothed.y - (tg.y + 0.8), flat) - 0.12,
          k.pitchMin,
          0.1,
        );
        cam.pitch += (wantPitch - cam.pitch) * Math.min(1, dt * 3);
      }

      // Arm direction: behind the look direction, offset over the right shoulder.
      const cy = Math.cos(cam.pitch);
      const fwd = { x: Math.sin(cam.yaw) * cy, y: Math.sin(cam.pitch), z: Math.cos(cam.yaw) * cy };
      const right = { x: -Math.cos(cam.yaw), z: Math.sin(cam.yaw) };
      // Shoulder offset, shortened if a wall is beside the creature, so the pivot never sits
      // inside geometry (a pivot inside a pillar puts the whole arm inside it).
      const side = Math.sign(k.shoulder) || 1;
      const sideDir = { x: right.x * side, y: 0, z: right.z * side };
      const wantShoulder = Math.abs(k.shoulder) * cam.scale;
      const sideHit = castRay(smoothed, sideDir, wantShoulder + k.collisionRadius);
      const shoulder = sideHit === null ? wantShoulder : Math.max(0, sideHit - k.collisionRadius);
      const pivot = {
        x: smoothed.x + sideDir.x * shoulder,
        y: smoothed.y,
        z: smoothed.z + sideDir.z * shoulder,
      };
      const want = k.distance * cam.scale;
      const back = { x: -fwd.x, y: -fwd.y, z: -fwd.z };
      // Three rays from the pivot approximate a sphere cast along the arm. Offset rays that start
      // inside geometry (hit at ~0) are ignored; the center ray always counts.
      let free = want;
      for (const off of [0, k.collisionRadius, -k.collisionRadius]) {
        const o = {
          x: pivot.x + right.x * off,
          y: pivot.y + off * 0.5,
          z: pivot.z + right.z * off,
        };
        const hit = castRay(o, back, want + k.collisionRadius);
        if (hit === null || (off !== 0 && hit < 0.02)) continue;
        free = Math.min(free, hit - k.collisionRadius);
      }
      free = Math.max(0.3, free);
      const rate = free < armLength ? k.pullIn : k.easeOut;
      armLength += (free - armLength) * Math.min(1, dt * rate);

      camera.position.set(
        pivot.x + back.x * armLength,
        pivot.y + back.y * armLength,
        pivot.z + back.z * armLength,
      );
      tmp.set(pivot.x + fwd.x * 10, pivot.y + fwd.y * 10, pivot.z + fwd.z * 10);
      camera.lookAt(tmp);

      // Juice: trauma² shake (smooth noise) and a sprint FOV kick.
      trauma = Math.max(0, trauma - dt * 1.4);
      const shake = reduceMotion ? 0 : trauma * trauma;
      if (shake > 0) {
        camera.rotation.z += 0.05 * shake * Math.sin(t * 37.1);
        camera.rotateX(0.03 * shake * Math.sin(t * 29.3 + 1.7));
        camera.rotateY(0.03 * shake * Math.sin(t * 31.7 + 4.1));
      }
      const fov = sprinting && !reduceMotion ? k.sprintFov : k.fov;
      if (Math.abs(camera.fov - fov) > 0.01) {
        camera.fov += (fov - camera.fov) * Math.min(1, dt * 5);
        camera.updateProjectionMatrix();
      }
    },
  };
  return cam;
}
